"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.groupAndRankByDivisionFlight = groupAndRankByDivisionFlight;
/**
 * Re-ranks a sorted subset of leaderboard rows within a leaf group.
 * Players with equal toPar (stroke) or totalPoints (stableford) share a rank.
 * Rank increments by number of tied players, not by 1.
 *
 * If a row carries an `adminRank > 0` (set by an organiser to break a tie),
 * that value is preserved as-is and the running rank counter is synchronised
 * to it so that subsequent calculated ranks remain consistent.
 */
function rerankRows(rows, isStableford) {
    let rank = 1;
    return rows.map((row, index) => {
        // Preserve organiser-set override ranks and keep the counter in sync.
        if (row.adminRank && row.adminRank > 0) {
            rank = row.adminRank;
            return { ...row, rank };
        }
        if (index === 0) {
            return { ...row, rank };
        }
        const prev = rows[index - 1];
        const sameScore = isStableford
            ? row.totalPoints === prev.totalPoints
            : row.toPar === prev.toPar;
        const bothStarted = row.holesCompleted > 0 && prev.holesCompleted > 0;
        if (!sameScore || !bothStarted) {
            rank = index + 1;
        }
        return { ...row, rank };
    });
}
/**
 * Groups already-sorted leaderboard rows by division (then by flight within each division)
 * and re-ranks players within each leaf group.
 *
 * Behaviour:
 * - Iterates divisions in the order defined by the organiser.
 * - Within each division, iterates flights in the defined order.
 * - If no divisions are configured but flights are, produces a single unnamed division.
 * - Players not matched to any division/flight fall into an "Unassigned" group.
 */
function groupAndRankByDivisionFlight({ leaderboardData, flighting, isStableford, }) {
    const divisions = flighting?.divisions ?? [];
    const flights = flighting?.flights ?? [];
    const hasDivisions = divisions.length > 0;
    const hasFlights = flights.length > 0;
    // Build the division list to iterate over
    const divisionList = hasDivisions
        ? divisions.map((d) => d.divisionName ?? d.name ?? String(d))
        : [''];
    const result = [];
    const assignedIds = new Set();
    for (const divisionName of divisionList) {
        const divisionRows = hasDivisions
            ? leaderboardData.filter((row) => row.playerInfo?.[0]?.division === divisionName)
            : leaderboardData;
        // Resolve the division-level prioritizeTeamName flag
        const divConfig = divisions.find((d) => (d.divisionName ?? d.name ?? String(d)) === divisionName);
        const divFlag = divConfig?.prioritizeTeamName ?? false;
        let flightGroups;
        if (hasFlights) {
            const flightList = flights.map((f) => f.flightName ?? f.name ?? String(f));
            flightGroups = [];
            const flightAssignedIds = new Set();
            for (const flightName of flightList) {
                const flightRows = divisionRows.filter((row) => row.playerInfo?.[0]?.flight === flightName);
                flightRows.forEach((r) => {
                    flightAssignedIds.add(r._id);
                    assignedIds.add(r._id);
                });
                // Flight-level flag overrides the division-level flag when explicitly set
                const flightConfig = flights.find((f) => (f.flightName ?? f.name ?? String(f)) === flightName);
                const resolvedFlag = flightConfig?.prioritizeTeamName ?? divFlag;
                flightGroups.push({
                    flightName,
                    rows: rerankRows(flightRows, isStableford),
                    prioritizeTeamName: resolvedFlag,
                });
            }
            // Unassigned within this division (has a division match but no flight match)
            const unassignedFlight = divisionRows.filter((row) => !flightAssignedIds.has(row._id));
            if (unassignedFlight.length > 0) {
                unassignedFlight.forEach((r) => assignedIds.add(r._id));
                flightGroups.push({
                    flightName: 'Unassigned',
                    rows: rerankRows(unassignedFlight, isStableford),
                    prioritizeTeamName: divFlag,
                });
            }
        }
        else {
            // No flights — single flight group containing all division rows
            divisionRows.forEach((r) => assignedIds.add(r._id));
            flightGroups = [
                {
                    flightName: '',
                    rows: rerankRows(divisionRows, isStableford),
                    prioritizeTeamName: divFlag,
                },
            ];
        }
        result.push({
            divisionName: divisionName || '',
            flights: flightGroups,
            prioritizeTeamName: divFlag,
        });
    }
    // Catch-all: players not matched to any defined division
    if (hasDivisions) {
        const unassigned = leaderboardData.filter((row) => !assignedIds.has(row._id));
        if (unassigned.length > 0) {
            result.push({
                divisionName: 'Unassigned',
                flights: [
                    {
                        flightName: '',
                        rows: rerankRows(unassigned, isStableford),
                    },
                ],
            });
        }
    }
    return result;
}
